<template>
  <LoadingDiv v-if="!isReady" />

  <form v-else class="rules-settings-form" @submit.prevent="handleSubmit">
    <section class="rules-form-section">
      <div class="rules-section-head">
        <span>Default Rules</span>
        <h5>Billing Defaults</h5>
        <p>Set the standard payment cycle used as the default when new policy rules are created.</p>
      </div>

      <div class="rules-field-grid">
        <div class="rules-field-card">
          <Settings2 :size="20" />
          <div class="setup-field">
            <input
              id="premiumCycle"
              type="number"
              v-model="premiumCycle"

              required
            />
            <label class="active" for="premiumCycle">Payment Cycle (Months)</label>
          </div>
        </div>
      </div>
    </section>

    <section class="rules-form-section">
      <div class="rules-section-head">
        <span>Membership Rules</span>
        <h5>Principal & Family Limits</h5>
        <p>Maintain the default family and age limits used when policy-specific limits are not yet set.</p>
      </div>

      <div class="rules-field-grid rules-limit-grid">
        <div class="rules-field-card">
          <Users :size="20" />
          <div class="setup-field">
            <input
              id="familySize"
              type="number"
              v-model="familySize"

              required
            />
            <label class="active" for="familySize">Family Size (Total)</label>
          </div>
        </div>

        <div class="rules-field-card">
          <UserRound :size="20" />
          <div class="setup-field">
            <input
              id="prinipalAge"
              type="number"
              v-model="principalAge"

              required
            />
            <label class="active" for="prinipalAge">Principal Age (Max)</label>
          </div>
        </div>

        <div class="rules-field-card">
          <UserRound :size="20" />
          <div class="setup-field">
            <input
              id="dependantAge"
              type="number"
              v-model="dependantAge"

              required
            />
            <label class="active" for="dependantAge">Dependant Age (Max)</label>
          </div>
        </div>
      </div>
    </section>

    <div class="rules-note">
      <Info :size="18" />
      <span>These values act as defaults. Policy-specific settings may still apply where a client policy has its own current rules.</span>
    </div>

    <div class="rules-form-actions">
      <button class="rules-primary-button" :disabled="isSubmitting">
        <span v-if="isSubmitting" class="rules-button-spinner" aria-hidden="true"></span>
        <CheckCircle2 v-else :size="17" />
        {{ isSubmitting ? "Updating..." : "Save Rules" }}
      </button>
    </div>
  </form>
</template>

<script>
import { onMounted, ref } from "vue";
import { CheckCircle2, Info, Settings2, UserRound, Users } from "@lucide/vue";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "HmoComponent",
  components: { CheckCircle2, Info, LoadingDiv, Settings2, UserRound, Users },
  emits: ["next"],
  setup(props, context) {
    // Workmen
    const response = ref([]);
    const error = ref(null);
    const isSubmitting = ref(false);
    const isReady = ref(false);

    // Rules table
    const premiumCycle = ref(null);
    const familySize = ref(null);
    const principalAge = ref(null);
    const dependantAge = ref(null);

    // start here
    onMounted(() => {
      // get items
      (async () => {
        const { res, err } = await getItems("rules");

        if (res.value) {
          // get
          response.value = res.value.data;

          // set
          response.value.forEach((x) => {
            if (x.setting == "premiumCycle" && premiumCycle.value == null) {
              // set
              premiumCycle.value = x.value;
            }

            if (x.setting == "familySize" && familySize.value == null) {
              // set
              familySize.value = x.value;
            }

            if (x.setting == "principalAge" && principalAge.value == null) {
              // set
              principalAge.value = x.value;
            }

            if (x.setting == "dependantAge" && dependantAge.value == null) {
              // set
              dependantAge.value = x.value;
            }
          });

          // is now Ready
          isReady.value = true;
        } else {
          error.value = err.value;
        }
      })();
    });

    // submit
    const handleSubmit = async () => {
      // start
      isSubmitting.value = true;
      error.value = false;

      const newVal = [
        {
          setting: "premiumCycle",
          value: premiumCycle.value,
          scope: 0,
        },
        {
          setting: "familySize",
          value: familySize.value,
          scope: 0,
        },
        {
          setting: "principalAge",
          value: principalAge.value,
          scope: 0,
        },
        {
          setting: "dependantAge",
          value: dependantAge.value,
          scope: 0,
        },
      ];

      // post item
      const { res, err } = await postItem("rules", newVal);

      if (res.value) {
        // next
        // emit new parameters
        context.emit("next", {
          step: 6,
          isComplete: true,
        });
      } else {
        error.value = err.value;
      }

      // end here
      isSubmitting.value = false;
    };

    return {
      error,
      isReady,
      isSubmitting,
      handleSubmit,
      premiumCycle,
      familySize,
      principalAge,
      dependantAge,
    };
  },
};
</script>

<style scoped>
.rules-settings-form {
  display: grid;
  gap: 18px;
}

.rules-form-section,
.rules-note {
  background: #ffffff;
  border: 1px solid #d3dde1;
  border-radius: 6px;
  overflow: hidden;
}

.rules-section-head {
  background: #fbfcfc;
  border-bottom: 1px solid #edf1f3;
  padding: 22px 24px;
}

.rules-section-head span {
  color: #00897b;
  display: block;
  font-size: 0.82rem;
  font-weight: 800;
  margin-bottom: 5px;
}

.rules-section-head h5 {
  color: #263238;
  font-size: 1.34rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.rules-section-head p {
  color: #607d8b;
  line-height: 1.5;
  margin: 8px 0 0;
}

.rules-field-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr);
  padding: 18px;
}

.rules-limit-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.rules-field-card {
  align-items: start;
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  display: grid;
  gap: 14px;
  grid-template-columns: 42px minmax(0, 1fr);
  padding: 16px 16px 6px;
}

.rules-field-card > svg {
  align-items: center;
  background: #eef4f5;
  border-radius: 6px;
  color: #00897b;
  display: flex;
  height: 42px;
  justify-content: center;
  width: 42px;
  margin-top: 6px;
}

.setup-field { display: flex; flex-direction: column-reverse; gap: .4rem; }
.rules-field-card input {
  background: var(--ui-background);
  border: 1px solid var(--ui-input);
  border-radius: calc(var(--ui-radius) - 2px);
  color: #263238;
  font: inherit;
  font-weight: 800;
  margin: 0;
  min-height: 2.6rem;
  padding: 0 .75rem;
  width: 100%;
}
.rules-field-card label {
  color: #607d8b;
  font-size: .75rem;
  font-weight: 700;
  position: static;
  transform: none;
}

.rules-note {
  align-items: center;
  background: #fbfcfc;
  color: #607d8b;
  display: flex;
  gap: 12px;
  padding: 16px 18px;
}

.rules-note svg {
  color: #00897b;
}

.rules-note span {
  font-weight: 700;
  line-height: 1.45;
}

.rules-form-actions {
  display: flex;
  justify-content: flex-end;
  padding: 6px 0 18px;
}

.rules-primary-button {
  align-items: center;
  background: #00897b;
  border: 1px solid #00897b;
  border-radius: 6px;
  color: #fff;
  display: inline-flex;
  gap: .5rem;
  font-weight: 800;
  letter-spacing: 0;
  min-height: 2.6rem;
  padding: 0 1rem;
  text-transform: none;
}
.rules-primary-button:disabled { cursor: wait; opacity: .6; }
.rules-button-spinner { animation: rules-spin .7s linear infinite; border: 2px solid rgb(255 255 255 / .35); border-radius: 50%; border-top-color: #fff; height: 1rem; width: 1rem; }
@keyframes rules-spin { to { transform: rotate(360deg); } }

@media only screen and (max-width: 992px) {
  .rules-limit-grid {
    grid-template-columns: 1fr;
  }
}
</style>
